/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';
import MailServerValidations from 'cio/utils/validations/mail-server';
import FormValidations from 'cio/utils/form-validations';
import espInfo, { EspInfo, EspKey, EspName, ESPS, ESP_AUTHENTICATION_OPTIONS } from 'cio/utils/email-service-providers';

import MailServerModel from 'cio/models/mail-server';

interface EditServerModalArgs {
  server: MailServerModel;
  onSave: (server: MailServerModel) => unknown;
  onClose: () => unknown;
}

export interface EspOption {
  label: EspName;
  logoUrl: string;
  value: EspKey;
}

export default class EditServerModal extends Component<EditServerModalArgs> {
  @tracked hasChanged = false;
  @tracked hasFailed = false;

  readonly authenticationOptions = ESP_AUTHENTICATION_OPTIONS;
  readonly validations = FormValidations.create({
    validations: MailServerValidations,
    context: this,
    modelKey: 'args.server',
  });

  EspKey = EspKey;

  @tracked selectedEsp: EspKey | null = null;

  constructor(owner: unknown, args: EditServerModalArgs) {
    super(owner, args);

    this.selectedEsp = args.server.selectedEsp;
  }

  get espOptions(): EspOption[] {
    return Object.entries(ESPS).map(([key, { name, logoUrl }]: [EspKey, EspInfo]) => ({
      logoUrl,
      label: name,
      value: key,
    }));
  }

  get selectedEspInfo() {
    if (this.selectedEsp == null) {
      return null;
    }

    return espInfo(this.selectedEsp);
  }

  get hasMultipleAddresses() {
    let info = this.selectedEspInfo;
    return typeof info?.address === 'object' && info?.address?.default != null;
  }

  get addresses() {
    if (!this.hasMultipleAddresses) {
      return [];
    }

    return Object.values(this.selectedEspInfo?.address ?? {}).map((a) => ({ label: a, value: a }));
  }

  saveTask = task(async () => {
    try {
      if (this.selectedEsp != null && this.selectedEsp !== EspKey.Other) {
        let isSendgrid = this.selectedEsp === 'sendgrid';

        let selectedEspInfo = espInfo(this.selectedEsp);

        this.args.server.setProperties({
          authentication: selectedEspInfo?.authentication,
          useApi: isSendgrid,
          port: selectedEspInfo?.port,
        });

        if (!this.hasMultipleAddresses) {
          this.args.server.set('address', selectedEspInfo?.address);
        }
      }

      await this.validations.validateModel();
      await this.args.onSave(this.args.server);
    } catch {
      this.hasFailed = true;
    }
  });

  @action
  cancel() {
    this.args.server.rollbackAttributes();
    this.args.onClose();
  }

  @action
  onChange(attribute: keyof MailServerModel, value: string) {
    this.hasChanged = true;
    this.hasFailed = false;

    this.args.server.set(attribute, value);

    if (!isEmpty((MailServerValidations as any)[attribute])) {
      this.validations.validateAttribute(attribute);
    }
  }

  @action
  onChangeProvider(option: EspOption) {
    this.selectedEsp = option.value;
    this.args.server.set('authentication', this.selectedEspInfo?.authentication);
  }
}
